// CREW-1289 — tells a tab that it is running an older build than the one
// deployed. The app is a single page: a tab left open across a deploy keeps
// its old code for as long as it stays open (after v2026.09.26 three browsers
// did, for 10–12 hours), and the deploy deletes the old build's chunks, so
// such a tab cannot open a route it has not loaded yet.
//
// The build writes version.json with the name of its entry chunk
// (vite-plugins/versionManifest.js); the tab compares it with the entry it
// was loaded with. Two reasons are reported: 'deployed' (version.json names
// another build) and 'chunk' (a chunk failed to load — Vite dispatches
// vite:preloadError for every failed dynamic import in a build).

export const CHECK_EVERY_MS = 5 * 60 * 1000;
// Returning to the tab checks at once, but switching back and forth does not
// turn into a request each time.
const MIN_GAP_MS = 30 * 1000;

// The entry chunk this page was loaded with, as the build names it
// ('assets/index-B8ZD-5dS.js'); null where no built entry is loaded — the
// dev server serves /src/index.jsx.
export const loadedEntry = (doc = document) => {
  const script = doc.querySelector('script[type="module"][src*="/assets/"]');
  if (!script) return null;

  return new URL(script.getAttribute('src'), 'http://x').pathname.replace(/^\//, '');
};

export const createVersionWatcher = ({
  fetchImpl = (...args) => fetch(...args),
  doc = document,
  win = window,
  entry = loadedEntry(doc),
} = {}) => {
  const listeners = new Set();
  let timer = null;
  let lastCheck = -Infinity;

  const notify = (reason) => listeners.forEach((listener) => listener(reason));

  const check = async () => {
    lastCheck = Date.now();
    try {
      const response = await fetchImpl(`/version.json?t=${lastCheck}`, { cache: 'no-store' });
      if (!response.ok) return;
      const { entry: deployed } = await response.json();
      if (deployed && deployed !== entry) notify('deployed');
    } catch {
      // offline, or a proxy answered with something else: the next check will tell
    }
  };

  const onTick = () => {
    if (doc.visibilityState === 'visible') check();
  };
  const onShown = () => {
    if (doc.visibilityState === 'visible' && Date.now() - lastCheck >= MIN_GAP_MS) check();
  };
  const onChunkError = () => notify('chunk');

  return {
    check,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    reportChunkFailure: onChunkError,
    start() {
      if (!entry || timer) return;
      timer = setInterval(onTick, CHECK_EVERY_MS);
      doc.addEventListener('visibilitychange', onShown);
      win.addEventListener('vite:preloadError', onChunkError);
    },
    stop() {
      clearInterval(timer);
      timer = null;
      doc.removeEventListener('visibilitychange', onShown);
      win.removeEventListener('vite:preloadError', onChunkError);
    },
  };
};

// The app's own watcher, started in src/index.jsx.
export const versionWatcher = createVersionWatcher();

// A route module that failed to load (AsyncComponent). In a build Vite has
// already reported it through vite:preloadError; this covers an import that
// did not go through Vite's preload wrapper.
export const reportChunkFailure = () => versionWatcher.reportChunkFailure();
